import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gasAdminLogin, acceptInvite, type ErrorResponse } from '@vn-propane/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { LanguageSelector } from '@/components/ui/language-selector';
import { LegalFooter } from '@vn-propane/shared/components';

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/auth/oauth/providers`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableProviders(data.data?.providers || []);
        }
      } catch {
        // Silently fail - OAuth just won't be available
      }
    };
    fetchProviders();
  }, []);

  // Handle OAuth callback results
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');

    if (oauthSuccess === 'true') {
      navigate('/organizations');
    } else if (oauthError) {
      setError(t('oauth.oauthError'));
    }
  }, [searchParams, navigate, t]);

  const handleGoogleLogin = () => {
    setIsOAuthLoading(true);
    const apiUrl = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiUrl}/auth/oauth/google/login?user_type=gas_admin`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await gasAdminLogin({
        body: { username, password },
      });

      if (result.data) {
        const inviteToken = searchParams.get('inviteToken');
        if (inviteToken) {
          // The user logged in to accept an invite. Consume the token before
          // landing them in the organizations list so they see the new org.
          const accepted = await acceptInvite({ body: { token: inviteToken } });
          if (accepted.data) {
            navigate(`/organizations/${accepted.data.data.organizationId}`);
            return;
          }
        }
        navigate('/organizations');
        return;
      }

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        setError(errorResponse.error?.message || t('validation.loginFailed'));
      } else {
        setError(t('validation.loginFailed'));
      }
    } catch (err) {
      setError(t('errors:unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 relative">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md mx-4 p-6 md:p-8 bg-background rounded-sm border shadow-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="Giao Gas" className="h-20 w-20 mb-3" />
          <h1 className="text-2xl font-extrabold tracking-tight text-center">
            giao<span className="text-propane-500">gas</span>
            <span className="block text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mt-1">
              {t('admin.login')}
            </span>
          </h1>
          <p className="text-muted-foreground text-sm text-center mt-2">
            {t('admin.loginDescription')}
          </p>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">{t('fields.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('fields.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>
          <Button type="submit" disabled={isLoading || isOAuthLoading} className="w-full">
            {isLoading ? t('signingIn') : t('signIn')}
          </Button>
        </form>

        {availableProviders.includes('google') && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t('oauth.or')}
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={isLoading || isOAuthLoading}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {isOAuthLoading ? t('oauth.signingInWithGoogle') : t('oauth.continueWithGoogle')}
            </Button>
          </>
        )}

        <p className="text-sm text-center mt-4 text-muted-foreground">
          {t('links.dontHaveAccount')}{' '}
          <Link to="/register" className="text-primary hover:underline">
            {t('register')}
          </Link>
        </p>
      </div>
      <LegalFooter className="absolute bottom-6" />
    </div>
  );
}
